import React from "react";
import { Tag, Space, Button, Popconfirm, Tooltip } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import DataTable from "../../components/DataTable";
import { renderTags } from "../../components/RenderTags";

const getRoleColor = (roleName) => {
  if (!roleName) return "default";
  const role = roleName.toLowerCase();
  if (role.includes("admin")) return "#49627a";
  if (role.includes("bot")) return "#e29b45";
  if (role === "user") return "#2b751d";
  return "purple";
};

const getTeamColor = () => "geekblue";

export default function FahrasUsersTable({
  data,
  loading,
  page,
  pageSize,
  onPageChange,
  onEditUser,
  onDeleteUser,
  isRoleUser,
}) {
  const columns = [
    {
      title: "Username",
      dataIndex: "name",
      key: "name",
      width: "12%",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Display Name",
      key: "displayName",
      width: "12%",
      render: (_, record) => record.displayName || record.name,
      sorter: (a, b) =>
        (a.displayName || "").localeCompare(b.displayName || ""),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: "15%",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: "Role",
      key: "baseRole",
      width: "10%",
      render: (_, record) => {
        let baseRole = "User";
        if (record.isAdmin) baseRole = "Admin";
        else if (record.isBot) baseRole = "Bot";
        return <Tag color={getRoleColor(baseRole)}>{baseRole}</Tag>;
      },
      sorter: (a, b) => {
        const getPriority = (r) => (r.isAdmin ? 3 : r.isBot ? 2 : 1);
        return getPriority(a) - getPriority(b);
      },
    },
    {
      title: "Teams",
      key: "teams",
      width: "15%",
      render: (_, record) => {
        const teamsList = Array.isArray(record.teams) ? record.teams : [];
        const teamNames = teamsList.map((t) => t.displayName || t.name);
        return renderTags(teamNames, getTeamColor);
      },
    },
    {
      title: "Permissions",
      key: "permissions",
      width: "20%",
      render: (_, record) => {
        const permissionNames = [];
        const extract = (arr) => {
          if (Array.isArray(arr)) {
            arr.forEach((r) => {
              const name = r.displayName || r.name;
              if (name && !permissionNames.includes(name))
                permissionNames.push(name);
            });
          }
        };
        extract(record.roles);
        extract(record.inheritedRoles);
        return renderTags(permissionNames, getRoleColor);
      },
    },
    {
      title: "Status",
      key: "status",
      width: "8%",
      render: (_, record) => (
        <Tag color={!record.deleted ? "success" : "error"}>
          {!record.deleted ? "Active" : "Deleted"}
        </Tag>
      ),
      sorter: (a, b) => (a.deleted === b.deleted ? 0 : a.deleted ? 1 : -1),
    },
  ];

  if (!isRoleUser) {
    columns.push({
      title: "Actions",
      key: "actions",
      width: "8%",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEditUser(record)}
            style={{ color: "#e29b45" }}
            title="Edit User"
          />
          <Popconfirm
            title="Delete User"
            description={`Are you sure you want to delete "${record.displayName || record.name}"?`}
            onConfirm={() => onDeleteUser(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete User"
            />
          </Popconfirm>
        </Space>
      ),
    });
  }

  return (
    <DataTable
      rowKey="id"
      columns={columns}
      data={data}
      loading={loading}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      scroll={{ x: 800 }}
    />
  );
}
